import type { FeeId } from "@@/shared/financials/fees/fee-id";
import { feeIdFactory, feeIdZodSchema } from "@@/shared/financials/fees/fee-id";
import type { Percentage } from "@@/shared/percentage";
import { percentageOfPrice } from "@@/shared/prices/percentage-of-price";
import type { Price } from "@@/shared/prices/price";
import {
    legacyCustomerServiceFeePrice,
    priceFactory,
    priceZodSchema,
} from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import type { Translatable } from "@@/shared/translations/translation";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { repeat } from "@@/utilities/repeat";
import type { Except } from "type-fest";
import { z } from "zod";
import { legacyServiceFeeIncludedCheck } from "./legacy-service-fee-included-check";
import type { Settlement } from "./settlement";

const hardCodedFeeIds = [
    "SMS_COSTS", // 1,50 kr per order eller orderändring
    "SMS_COSTS_2KR_FEE", // 2 kr per order eller orderändring
    "SMS_COSTS_2_50KR_FEE", // 2,50 kr per order eller orderändring
    "SMS_COSTS_3KR_FEE", // 3 kr per order eller orderändring
    "SMS_COSTS_3_50KR_FEE", // 3_50 kr per order eller orderändring
    "SMS_COSTS_4KR_FEE", // 4_50 kr per order eller orderändring
    "SMS_COSTS_4_50KR_FEE", // 4_50 kr per order eller orderändring
    "SMS_COSTS_5KR_FEE", // 5_50 kr per order eller orderändring
    "SMS_COSTS_5_50KR_FEE", // 5_50 kr per order eller orderändring
    "SMS_COSTS_6KR_FEE", // 6_50 kr per order eller orderändring
    "SMS_COSTS_6_50KR_FEE", // 6_50 kr per order eller orderändring
    "SMS_COSTS_7KR_FEE", // 7_50 kr per order eller orderändring
    "SMS_COSTS_7_50KR_FEE", // 7_50 kr per order eller orderändring
    "SMS_COSTS_8KR_FEE", // 8_50 kr per order eller orderändring
    "SMS_COSTS_8_50KR_FEE", // 8_50 kr per order eller orderändring
    "SMS_COSTS_9KR_FEE", // 9_50 kr per order eller orderändring
    "SMS_COSTS_9_50KR_FEE", // 9_50 kr per order eller orderändring
    "SMS_COSTS_10KR_FEE", // 10_50 kr per order eller orderändring
    "SMS_COSTS_10_50KR_FEE", // 10_50 kr per order eller orderändring
    "SMS_COSTS_LEGACY_PER_TRANSACTION",
    "PAYMENT_COSTS",
    "PAYMENT_COSTS__EXCL_VAT",
    "PAYMENT_COSTS_STRIPE_STYLE_FOR_ALL",
    "VARIABLE_0_25_PERCENT_MIN_1KR_FEE",
    "VARIABLE_1_PERCENT_MIN_1KR_FEE",
    "VARIABLE_1_5_PERCENT_MIN_1_5KR_FEE",
    "VARIABLE_2_PERCENT_MIN_2KR_FEE",
    "VARIABLE_2_PERCENT_MIN_2KR_FEE__EXCL_VAT",
    "VARIABLE_3_PERCENT_MIN_3KR_FEE",
    "VARIABLE_3_PERCENT_MIN_3KR_FEE__EXCL_VAT",
    "VARIABLE_4_PERCENT_MIN_4KR_FEE",
    "VARIABLE_4_PERCENT_MIN_4KR_FEE__EXCL_VAT",
    "VARIABLE_5_PERCENT_MIN_5KR_FEE",
    "VARIABLE_5_PERCENT_MIN_5KR_FEE__EXCL_VAT",
    "VARIABLE_6_PERCENT_MIN_6KR_FEE",
    "VARIABLE_6_PERCENT_MIN_6KR_FEE__EXCL_VAT",
    "VARIABLE_7_PERCENT_MIN_7KR_FEE",
    "VARIABLE_7_PERCENT_MIN_7KR_FEE__EXCL_VAT",
    "VARIABLE_10_PERCENT_MIN_3KR_FEE",
    "VARIABLE_10_PERCENT_MIN_3KR_FEE__EXCL_VAT",
    "VARIABLE_10_PERCENT_MIN_10KR_FEE",
    "VARIABLE_10_PERCENT_MIN_10KR_FEE__EXCL_VAT",
    "VARIABLE_0_15_PERCENT_FEE",
    "VARIABLE_0_15_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_1_PERCENT_FEE",
    "VARIABLE_1_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_2_PERCENT_FEE",
    "VARIABLE_2_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_3_PERCENT_FEE",
    "VARIABLE_3_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_4_PERCENT_FEE",
    "VARIABLE_4_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_5_PERCENT_FEE",
    "VARIABLE_5_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_6_PERCENT_FEE",
    "VARIABLE_6_PERCENT_FEE__EXCL_VAT",
    "VARIABLE_10_PERCENT_FEE",
    "VARIABLE_10_PERCENT_FEE__EXCL_VAT",
    "FIXED_599_KR_FEE",
    "FIXED_895_KR_FEE",
    "FIXED_1_900_KR_FEE",
    "FIXED_3_900_KR_FEE",
    "FIXED_9_000_KR_FEE",
    "FIXED_15_000_KR_FEE",
    "FIXED_IPAD_RENTAL_150_KR_FEE",
    "FIXED_PRINTER_RENTAL_150_KR_FEE",
] as const;

type HardCodedFeeId = (typeof hardCodedFeeIds)[number];
const hardCodedFeeIdZodEnum = z.enum(hardCodedFeeIds);

type HardCodedFee = {
    _id: FeeId;
    _hardCodedId: HardCodedFeeId;
    _type: "HARD_CODED_FEE";
    title: Translatable;
    price: Price;
    description?: Translatable;
};
const hardCodedFeeZodSchema = z.object({
    _id: feeIdZodSchema,
    _hardCodedId: hardCodedFeeIdZodEnum,
    _type: z.literal("HARD_CODED_FEE"),
    title: translatableZodSchema,
    price: priceZodSchema,
    description: translatableZodSchema.optional(),
});
type FactoryParams = Except<HardCodedFee, "_id" | "_type">;
const hardCodedFeeFactory = (params: FactoryParams): HardCodedFee => {
    return {
        ...params,
        _id: feeIdFactory(),
        _type: "HARD_CODED_FEE",
    };
};

const smsCostTemplateFee = (
    pricePerOrder: Price,
    settlements: Settlement[],
    legacyIncludeRefunds = false,
) => {
    const ordersCreatedOrChangedAndPaymentFulfilledCount = settlements
        .filter(
            settlement => legacyIncludeRefunds || settlement.type === "PAYMENT",
        )
        .map(settlement => settlement.orderId).length;

    return sumPrices(
        repeat(pricePerOrder, ordersCreatedOrChangedAndPaymentFulfilledCount),
    );
};

const paymentCostFee = (
    settlements: Settlement[],
    /**
     * Denna ska/bör nog inte användas, men utifall att vi har någon
     * Leverantör som haft detta felaktigt förr, så kan vi använda denna om vi vill:
     */
    basedOn:
        | "AMOUNT_INCLUDING_VAT"
        | "AMOUNT_EXCLUDING_VAT" = "AMOUNT_INCLUDING_VAT",
) => {
    // Per betalning via stripe, 1,5% + 1,80 kr + 10%
    // Per betalning via swish, 3kr
    const stripeCost = sumPrices(
        settlements
            .filter(
                settlement =>
                    settlement.paymentType === "STRIPE" &&
                    settlement.type === "PAYMENT",
            )
            .map(settlement => {
                //! If there are multiple payments for the same order by mistake
                //! we don't handle that yet, and don't know how we want to handle it either
                const price =
                    settlement.originEvent.type === "CREATED"
                        ? legacyServiceFeeIncludedCheck(settlement)
                            ? settlement.type === "PAYMENT"
                                ? subtractFromPrice(settlement.price, [
                                      legacyCustomerServiceFeePrice,
                                  ])
                                : sumPrices([
                                      settlement.price,
                                      legacyCustomerServiceFeePrice,
                                  ])
                            : settlement.price
                        : settlement.price;

                const percentageFee = priceFactory({
                    amountExcludingVat:
                        basedOn === "AMOUNT_INCLUDING_VAT"
                            ? price.amountIncludingVat * (0.015 as Percentage)
                            : price.amountExcludingVat * (0.015 as Percentage),
                });

                const fixedFee = priceFactory({ amountExcludingVat: 180 });
                const totalFee = sumPrices([percentageFee, fixedFee]);
                const result = percentageOfPrice(totalFee, 1.1 as Percentage); // add 10%
                return result;
            }),
    );

    const swishCost = sumPrices(
        repeat(
            priceFactory({ amountExcludingVat: 300 }),
            settlements.filter(
                settlement =>
                    settlement.paymentType === "SWISH" &&
                    settlement.type === "PAYMENT",
            ).length,
        ),
    );

    return sumPrices([stripeCost, swishCost]);
};

const paymentCostLikeStripeForAllFee = (
    settlements: Settlement[],
    /**
     * Denna ska/bör nog inte användas, men utifall att vi har någon
     * Leverantör som haft detta felaktigt förr, så kan vi använda denna om vi vill:
     */
    basedOn:
        | "AMOUNT_INCLUDING_VAT"
        | "AMOUNT_EXCLUDING_VAT" = "AMOUNT_INCLUDING_VAT",
) => {
    // Per betalning via stripe ELLER SWISH, 1,5% + 1,80 kr + 10%
    const paymentCost = sumPrices(
        settlements
            .filter(
                settlement =>
                    settlement.type === "PAYMENT" &&
                    (settlement.paymentType === "STRIPE" ||
                        settlement.paymentType === "SWISH"),
            )
            .map(settlement => {
                //! If there are multiple payments for the same order by mistake
                //! we don't handle that yet, and don't know how we want to handle it either
                const price =
                    settlement.originEvent.type === "CREATED"
                        ? legacyServiceFeeIncludedCheck(settlement)
                            ? settlement.type === "PAYMENT"
                                ? subtractFromPrice(settlement.price, [
                                      legacyCustomerServiceFeePrice,
                                  ])
                                : sumPrices([
                                      settlement.price,
                                      legacyCustomerServiceFeePrice,
                                  ])
                            : settlement.price
                        : settlement.price;

                const percentageFee = priceFactory({
                    amountExcludingVat:
                        basedOn === "AMOUNT_INCLUDING_VAT"
                            ? price.amountIncludingVat * (0.015 as Percentage)
                            : price.amountExcludingVat * (0.015 as Percentage),
                });

                const fixedFee = priceFactory({ amountExcludingVat: 180 });
                const totalFee = sumPrices([percentageFee, fixedFee]);
                const result = percentageOfPrice(totalFee, 1.1 as Percentage); // add 10%
                return result;
            }),
    );

    return paymentCost;
};

const percentageFee = (
    settlements: Settlement[],
    percentage: Percentage,
    basedOn:
        | "AMOUNT_INCLUDING_VAT"
        | "AMOUNT_EXCLUDING_VAT" = "AMOUNT_INCLUDING_VAT",
) => {
    // Per payment, add a fee of percentage given of the payment amount, or min, whichever is higher
    // If min is not given, use 0
    const result = sumPrices(
        settlements
            .filter(
                settlement =>
                    settlement.paymentType !== "FREE" &&
                    settlement.paymentType !== "ON_PREMISE",
            )
            .map(settlement => {
                const price =
                    settlement.originEvent.type === "CREATED"
                        ? legacyServiceFeeIncludedCheck(settlement)
                            ? settlement.type === "PAYMENT"
                                ? subtractFromPrice(settlement.price, [
                                      legacyCustomerServiceFeePrice,
                                  ])
                                : sumPrices([
                                      settlement.price,
                                      legacyCustomerServiceFeePrice,
                                  ])
                            : settlement.price
                        : settlement.price;

                const percentageFee = priceFactory({
                    amountExcludingVat:
                        basedOn === "AMOUNT_INCLUDING_VAT"
                            ? price.amountIncludingVat * percentage
                            : price.amountExcludingVat * percentage,
                });

                return percentageFee;
            }),
    );

    return result;
};
const variableFeePerPayment = (
    settlements: Settlement[],
    percentage: Percentage,
    minFixedFee?: Price,
    basedOn:
        | "AMOUNT_INCLUDING_VAT"
        | "AMOUNT_EXCLUDING_VAT" = "AMOUNT_INCLUDING_VAT",
) => {
    // Per payment, add a fee of percentage given of the payment amount, or min, whichever is higher
    // If min is not given, use 0
    const variableFee = sumPrices(
        settlements
            .filter(
                settlement =>
                    settlement.type === "PAYMENT" &&
                    settlement.paymentType !== "FREE" &&
                    settlement.paymentType !== "ON_PREMISE",
            )
            .map(settlement => {
                const price =
                    settlement.originEvent.type === "CREATED"
                        ? legacyServiceFeeIncludedCheck(settlement)
                            ? settlement.type === "PAYMENT"
                                ? subtractFromPrice(settlement.price, [
                                      legacyCustomerServiceFeePrice,
                                  ])
                                : sumPrices([
                                      settlement.price,
                                      legacyCustomerServiceFeePrice,
                                  ])
                            : settlement.price
                        : settlement.price;

                const percentageFee = priceFactory({
                    amountExcludingVat:
                        basedOn === "AMOUNT_INCLUDING_VAT"
                            ? price.amountIncludingVat * percentage
                            : price.amountExcludingVat * percentage,
                });

                const result =
                    minFixedFee &&
                    minFixedFee.amountIncludingVat >
                        percentageFee.amountIncludingVat
                        ? minFixedFee
                        : percentageFee;
                return result;
            }),
    );

    return variableFee;
};

const hardCodedFeeMeta: Record<
    HardCodedFeeId,
    {
        _hardCodedId: HardCodedFeeId;
        title: Translatable;
        descriptiveTitle: Translatable;
        description: Translatable;
    }
> = {
    SMS_COSTS: {
        _hardCodedId: "SMS_COSTS",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 1,50kr",
        description: `SMS-kostnad på 1,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_2KR_FEE: {
        _hardCodedId: "SMS_COSTS_2KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 2kr",
        description: `SMS-kostnad på 2 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_2_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_2_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 2,50kr",
        description: `SMS-kostnad på 2,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_3KR_FEE: {
        _hardCodedId: "SMS_COSTS_3KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 3kr",
        description: `SMS-kostnad på 3 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_3_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_3_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 3,50kr",
        description: `SMS-kostnad på 3,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_4KR_FEE: {
        _hardCodedId: "SMS_COSTS_4KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 4kr",
        description: `SMS-kostnad på 4 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_4_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_4_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 4,50kr",
        description: `SMS-kostnad på 4,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_5KR_FEE: {
        _hardCodedId: "SMS_COSTS_5KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 5kr",
        description: `SMS-kostnad på 5 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_5_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_5_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad (Schablon) 5,50kr",
        description: `SMS-kostnad på 5,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_6KR_FEE: {
        _hardCodedId: "SMS_COSTS_6KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 6kr (Schablon)",
        description: `SMS-kostnad på 6 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_6_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_6_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 6,50kr (Schablon)",
        description: `SMS-kostnad på 6,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_7KR_FEE: {
        _hardCodedId: "SMS_COSTS_7KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 7kr (Schablon)",
        description: `SMS-kostnad på 7 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_7_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_7_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 7,50kr (Schablon)",
        description: `SMS-kostnad på 7,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_8KR_FEE: {
        _hardCodedId: "SMS_COSTS_8KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 8kr (Schablon)",
        description: `SMS-kostnad på 8 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_8_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_8_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 8,50kr (Schablon)",
        description: `SMS-kostnad på 8,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_9KR_FEE: {
        _hardCodedId: "SMS_COSTS_9KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 9kr (Schablon)",
        description: `SMS-kostnad på 9 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_9_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_9_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 9,50kr (Schablon)",
        description: `SMS-kostnad på 9,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_10KR_FEE: {
        _hardCodedId: "SMS_COSTS_10KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 10kr (Schablon)",
        description: `SMS-kostnad på 10kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_10_50KR_FEE: {
        _hardCodedId: "SMS_COSTS_10_50KR_FEE",
        title: "SMS-kostnad",
        descriptiveTitle: "SMS-kostnad 10,50 kr (Schablon)",
        description: `SMS-kostnad på 10,50 kr per order eller orderändring oavsett om gratis eller betald`,
    },
    SMS_COSTS_LEGACY_PER_TRANSACTION: {
        _hardCodedId: "SMS_COSTS_LEGACY_PER_TRANSACTION",
        title: "SMS-kostnad",
        descriptiveTitle:
            "SMS-kostnad 1,50 kr per transaktion (Schablon, inkl. återbetalningar)",
        description: `SMS-kostnad på 1,50 kr per transaktion`,
    },
    PAYMENT_COSTS: {
        _hardCodedId: "PAYMENT_COSTS",
        title: "Betalkostnader",
        descriptiveTitle: "Betalkostnader (stripe, swish)",
        description: `Betalkostnad på 1,5% + 1,80 kr + 10% per betalning via Stripe, och 3kr per betalning via Swish`,
    },
    PAYMENT_COSTS__EXCL_VAT: {
        _hardCodedId: "PAYMENT_COSTS__EXCL_VAT",
        title: "Betalkostnader",
        descriptiveTitle: "Betalkostnader (ber. på exkl. moms)",
        description: `Betalkostnad på 1,5% + 1,80 kr + 10% per betalning via Stripe, och 3kr per betalningssumma exkl. moms via Swish`,
    },
    PAYMENT_COSTS_STRIPE_STYLE_FOR_ALL: {
        _hardCodedId: "PAYMENT_COSTS_STRIPE_STYLE_FOR_ALL",
        title: "Betalkostnader",
        descriptiveTitle: "Betalkostnader (stripe-kostnad för allt)",
        description: `Betalkostnad på 1,5% + 1,80 kr + 10% per betalning`,
    },
    VARIABLE_0_25_PERCENT_MIN_1KR_FEE: {
        _hardCodedId: "VARIABLE_0_25_PERCENT_MIN_1KR_FEE",
        title: "Rörlig avg. 0,25% (min 1kr)",
        descriptiveTitle: "Rörlig avg. 0,25% (min 1kr)",
        description: `Rörlig avgift på 0,25% eller minst 1kr (exkl. moms) per betalning`,
    },
    VARIABLE_1_PERCENT_MIN_1KR_FEE: {
        _hardCodedId: "VARIABLE_1_PERCENT_MIN_1KR_FEE",
        title: "Rörlig avg. 1% (min 1kr)",
        descriptiveTitle: "Rörlig avg. 1% (min 1kr)",
        description: `Rörlig avgift på 1% eller minst 1kr (exkl. moms) per betalning`,
    },
    VARIABLE_1_5_PERCENT_MIN_1_5KR_FEE: {
        _hardCodedId: "VARIABLE_1_5_PERCENT_MIN_1_5KR_FEE",
        title: "Rörlig avg. 1.5% (min 1.5kr)",
        descriptiveTitle: "Rörlig avg. 1.5% (min 1.5kr)",
        description: `Rörlig avgift på 1.5% eller minst 1.5kr (exkl. moms) per betalning`,
    },
    VARIABLE_2_PERCENT_MIN_2KR_FEE: {
        _hardCodedId: "VARIABLE_2_PERCENT_MIN_2KR_FEE",
        title: "Rörlig avg. 2% (min 2kr)",
        descriptiveTitle: "Rörlig avg. 2% (min 2kr)",
        description: `Rörlig avgift på 2% eller minst 2kr (exkl. moms) per betalning`,
    },
    VARIABLE_2_PERCENT_MIN_2KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_2_PERCENT_MIN_2KR_FEE__EXCL_VAT",
        title: "Rörlig avg. 2% (min 2kr)",
        descriptiveTitle: "Rörlig avg. 2% (min 2kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 2% eller minst 2kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_3_PERCENT_MIN_3KR_FEE: {
        _hardCodedId: "VARIABLE_3_PERCENT_MIN_3KR_FEE",
        title: "Rörlig avg. 3% (min 3kr)",
        descriptiveTitle: "Rörlig avg. 3% (min 3kr)",
        description: `Rörlig avgift på 3% eller minst 3kr (exkl. moms) per betalning`,
    },
    VARIABLE_3_PERCENT_MIN_3KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_3_PERCENT_MIN_3KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 3% (min 3kr)",
        descriptiveTitle: "Rörlig avg. på 3% (min 3kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 3% eller minst 3kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_4_PERCENT_MIN_4KR_FEE: {
        _hardCodedId: "VARIABLE_4_PERCENT_MIN_4KR_FEE",
        title: "Rörlig avg. 4% (min 4kr)",
        descriptiveTitle: "Rörlig avg. 4% (min 4kr)",
        description: `Rörlig avgift på 4% eller minst 4kr (exkl. moms) per betalning`,
    },
    VARIABLE_4_PERCENT_MIN_4KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_4_PERCENT_MIN_4KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 4% (min 4kr)",
        descriptiveTitle: "Rörlig avg. på 4% (min 4kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 4% eller minst 4kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_5_PERCENT_MIN_5KR_FEE: {
        _hardCodedId: "VARIABLE_5_PERCENT_MIN_5KR_FEE",
        title: "Rörlig avg. 5% (min 5kr)",
        descriptiveTitle: "Rörlig avg. 5% (min 5kr)",
        description: `Rörlig avgift på 5% eller minst 5kr (exkl. moms) per betalning`,
    },
    VARIABLE_5_PERCENT_MIN_5KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_5_PERCENT_MIN_5KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 5% (min 5kr)",
        descriptiveTitle: "Rörlig avg. på 5% (min 5kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 5% eller minst 3kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_6_PERCENT_MIN_6KR_FEE: {
        _hardCodedId: "VARIABLE_6_PERCENT_MIN_6KR_FEE",
        title: "Rörlig avg. 6% (min 6kr)",
        descriptiveTitle: "Rörlig avg. 6% (min 6kr)",
        description: `Rörlig avgift på 6% eller minst 6kr (exkl. moms) per betalning`,
    },
    VARIABLE_6_PERCENT_MIN_6KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_6_PERCENT_MIN_6KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 6% (min 6kr)",
        descriptiveTitle: "Rörlig avg. på 6% (min 6kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 6% eller minst 6kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_7_PERCENT_MIN_7KR_FEE: {
        _hardCodedId: "VARIABLE_7_PERCENT_MIN_7KR_FEE",
        title: "Rörlig avg. 7% (min 7kr)",
        descriptiveTitle: "Rörlig avg. 7% (min 7kr)",
        description: `Rörlig avgift på 7% eller minst 7kr (exkl. moms) per betalning`,
    },
    VARIABLE_7_PERCENT_MIN_7KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_7_PERCENT_MIN_7KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 7% (min 7kr)",
        descriptiveTitle: "Rörlig avg. på 7% (min 7kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 7% eller minst 7kr (exkl. moms) per betalningssumman exkl. moms`,
    },

    VARIABLE_10_PERCENT_MIN_3KR_FEE: {
        _hardCodedId: "VARIABLE_10_PERCENT_MIN_3KR_FEE",
        title: "Rörlig avg. 10% (min 3kr)",
        descriptiveTitle: "Rörlig avg. 10% (min 3kr)",
        description: `Rörlig avgift på 10% eller minst 3kr (exkl. moms) per betalning`,
    },
    VARIABLE_10_PERCENT_MIN_3KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_10_PERCENT_MIN_3KR_FEE__EXCL_VAT",
        title: "Rörlig avg. på 10% (min 3kr)",
        descriptiveTitle: "Rörlig avg. på 10% (min 3kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 10% eller minst 3kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_10_PERCENT_MIN_10KR_FEE: {
        _hardCodedId: "VARIABLE_10_PERCENT_MIN_10KR_FEE",
        title: "Rörlig avg. 10% (min 10kr)",
        descriptiveTitle: "Rörlig avg. 10% (min 10kr)",
        description: `Rörlig avgift på 10% eller minst 10kr (exkl. moms) per betalning`,
    },
    VARIABLE_10_PERCENT_MIN_10KR_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_10_PERCENT_MIN_10KR_FEE__EXCL_VAT",
        title: "Rörlig avg. 10% (min 10kr)",
        descriptiveTitle: "Rörlig avg. 10% (min 10kr) (ber. på exkl. moms)",
        description: `Rörlig avgift på 10% eller minst 10kr (exkl. moms) per betalningssumman exkl. moms`,
    },
    VARIABLE_0_15_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_0_15_PERCENT_FEE",
        title: "Rörlig avg. 0,15%",
        descriptiveTitle: "Rörlig avg. 0,15%",
        description: `Rörlig avgift på 0,15% per betalning`,
    },
    VARIABLE_0_15_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_0_15_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. på 0,15%",
        descriptiveTitle: "Rörlig avg. på 0,15% (ber. på exkl. moms)",
        description: `Rörlig avgift på 0,15% per betalningssumman exkl. moms`,
    },
    VARIABLE_1_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_1_PERCENT_FEE",
        title: "Rörlig avg. 1%",
        descriptiveTitle: "Rörlig avg. 1%",
        description: `Rörlig avgift på 1% per nota`,
    },
    VARIABLE_1_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_1_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 1%",
        descriptiveTitle: "Rörlig avg. 1% (ber. på exkl. moms)",
        description: `Rörlig avgift på 1% per nota exkl. moms`,
    },
    VARIABLE_2_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_2_PERCENT_FEE",
        title: "Rörlig avg. 2%",
        descriptiveTitle: "Rörlig avg. 2%",
        description: `Rörlig avgift på 2% per nota`,
    },
    VARIABLE_2_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_2_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 2%",
        descriptiveTitle: "Rörlig avg. 2% (ber. på exkl. moms)",
        description: `Rörlig avgift på 2% per nota exkl. moms`,
    },
    VARIABLE_3_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_3_PERCENT_FEE",
        title: "Rörlig avg. 3%",
        descriptiveTitle: "Rörlig avg. 3%",
        description: `Rörlig avgift på 3% per nota`,
    },
    VARIABLE_3_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_3_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 3%",
        descriptiveTitle: "Rörlig avg. 3% (ber. på exkl. moms)",
        description: `Rörlig avgift på 3% per nota exkl. moms`,
    },
    VARIABLE_4_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_4_PERCENT_FEE",
        title: "Rörlig avg. 4%",
        descriptiveTitle: "Rörlig avg. 4%",
        description: `Rörlig avgift på 4% per nota`,
    },
    VARIABLE_4_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_4_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 4%",
        descriptiveTitle: "Rörlig avg. 4% (ber. på exkl. moms)",
        description: `Rörlig avgift på 4% per nota exkl. moms`,
    },
    VARIABLE_5_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_5_PERCENT_FEE",
        title: "Rörlig avg. 5%",
        descriptiveTitle: "Rörlig avg. 5%",
        description: `Rörlig avgift på 5% per nota`,
    },
    VARIABLE_5_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_5_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 5%",
        descriptiveTitle: "Rörlig avg. 5% (ber. på exkl. moms)",
        description: `Rörlig avgift på 5% per nota exkl. moms`,
    },
    VARIABLE_6_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_6_PERCENT_FEE",
        title: "Rörlig avg. 6%",
        descriptiveTitle: "Rörlig avg. 6%",
        description: `Rörlig avgift på 6% per nota`,
    },
    VARIABLE_6_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_6_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 6%",
        descriptiveTitle: "Rörlig avg. 6% (ber. på exkl. moms)",
        description: `Rörlig avgift på 6% per nota exkl. moms`,
    },
    VARIABLE_10_PERCENT_FEE: {
        _hardCodedId: "VARIABLE_10_PERCENT_FEE",
        title: "Rörlig avg. 10%",
        descriptiveTitle: "Rörlig avg. 10%",
        description: `Rörlig avgift på 10% per nota`,
    },
    VARIABLE_10_PERCENT_FEE__EXCL_VAT: {
        _hardCodedId: "VARIABLE_10_PERCENT_FEE__EXCL_VAT",
        title: "Rörlig avg. 10%",
        descriptiveTitle: "Rörlig avg. 10% (ber. på exkl. moms)",
        description: `Rörlig avgift på 10% per nota exkl. moms`,
    },
    FIXED_599_KR_FEE: {
        _hardCodedId: "FIXED_599_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 599 kr",
        description: `Fast månadsavgift på 599 kr`,
    },
    FIXED_895_KR_FEE: {
        _hardCodedId: "FIXED_895_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 895 kr",
        description: `Fast månadsavgift på 895 kr`,
    },
    FIXED_1_900_KR_FEE: {
        _hardCodedId: "FIXED_1_900_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 1 900 kr",
        description: `Fast månadsavgift på 1 900 kr`,
    },
    FIXED_3_900_KR_FEE: {
        _hardCodedId: "FIXED_3_900_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 3 900 kr",
        description: `Fast månadsavgift på 3 900 kr`,
    },
    FIXED_9_000_KR_FEE: {
        _hardCodedId: "FIXED_9_000_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 9 000 kr",
        description: `Fast månadsavgift på 9 000 kr`,
    },
    FIXED_15_000_KR_FEE: {
        _hardCodedId: "FIXED_15_000_KR_FEE",
        title: "Fast avg.",
        descriptiveTitle: "Fast avg. 15 000 kr",
        description: `Fast månadsavgift på 15 000 kr`,
    },
    FIXED_IPAD_RENTAL_150_KR_FEE: {
        _hardCodedId: "FIXED_IPAD_RENTAL_150_KR_FEE",
        title: "iPad hyra",
        descriptiveTitle: "iPad hyra (150 kr exkl. moms)",
        description: `iPad hyra på 150 kr per månad`,
    },
    FIXED_PRINTER_RENTAL_150_KR_FEE: {
        _hardCodedId: "FIXED_PRINTER_RENTAL_150_KR_FEE",
        title: "Kvittoskrivare",
        descriptiveTitle: "Kvittoskrivare (150 kr exkl. moms)",
        description: `Kvittoskrivare hyra på 150 kr per månad`,
    },
};

const hardCodedFeesForSettlements = (
    settlements: Settlement[],
): Record<HardCodedFeeId, HardCodedFee> => {
    return {
        // Sms kostnad på 1,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 150 }),
                settlements,
            ),
        }),
        // Sms kostnad på 2 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_2KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_2KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 200 }),
                settlements,
            ),
        }),
        // Sms kostnad på 2,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_2_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_2_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 250 }),
                settlements,
            ),
        }),
        // Sms kostnad på 3 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_3KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_3KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 300 }),
                settlements,
            ),
        }),
        // Sms kostnad på 3,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_3_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_3_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 350 }),
                settlements,
            ),
        }),
        // Sms kostnad på 4 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_4KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_4KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 400 }),
                settlements,
            ),
        }),
        // Sms kostnad på 4,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_4_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_4_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 450 }),
                settlements,
            ),
        }),
        // Sms kostnad på 5 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_5KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_5KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 500 }),
                settlements,
            ),
        }),
        // Sms kostnad på 5,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_5_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_5_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 550 }),
                settlements,
            ),
        }),
        // Sms kostnad på 6 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_6KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_6KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 600 }),
                settlements,
            ),
        }),
        // Sms kostnad på 6,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_6_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_6_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 650 }),
                settlements,
            ),
        }),
        // Sms kostnad på 7 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_7KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_7KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 700 }),
                settlements,
            ),
        }),
        // Sms kostnad på 7,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_7_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_7_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 750 }),
                settlements,
            ),
        }),
        // Sms kostnad på 8 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_8KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_8KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 800 }),
                settlements,
            ),
        }),
        // Sms kostnad på 8,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_8_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_8_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 850 }),
                settlements,
            ),
        }),
        // Sms kostnad på 9 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_9KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_9KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 900 }),
                settlements,
            ),
        }),
        // Sms kostnad på 9,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_9_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_9_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 950 }),
                settlements,
            ),
        }),
        // Sms kostnad på 10 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_10KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_10KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 1000 }),
                settlements,
            ),
        }),
        // Sms kostnad på 10,50 kr per order eller orderändring, oavsett om gratis, intern eller "vanlig" order
        SMS_COSTS_10_50KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_10_50KR_FEE,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 1050 }),
                settlements,
            ),
        }),
        SMS_COSTS_LEGACY_PER_TRANSACTION: hardCodedFeeFactory({
            ...hardCodedFeeMeta.SMS_COSTS_LEGACY_PER_TRANSACTION,
            price: smsCostTemplateFee(
                priceFactory({ amountExcludingVat: 150 }),
                settlements,
                true,
            ),
        }),
        // Betalkostnader
        PAYMENT_COSTS: hardCodedFeeFactory({
            ...hardCodedFeeMeta.PAYMENT_COSTS,
            price: paymentCostFee(settlements),
        }),
        PAYMENT_COSTS_STRIPE_STYLE_FOR_ALL: hardCodedFeeFactory({
            ...hardCodedFeeMeta.PAYMENT_COSTS_STRIPE_STYLE_FOR_ALL,
            price: paymentCostLikeStripeForAllFee(settlements),
        }),
        //! Denna ska/bör nog inte användas, men utifall att vi har någon
        //! Leverantör som haft detta felaktigt förr, så kan vi använda denna om vi vill:
        PAYMENT_COSTS__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.PAYMENT_COSTS__EXCL_VAT,
            price: paymentCostFee(settlements, "AMOUNT_EXCLUDING_VAT"),
        }),
        // Rörliga avgifter på exempelvis 2% eller minst 2kr (exkl. moms) per betalning
        VARIABLE_0_15_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_0_15_PERCENT_FEE,
            price: percentageFee(settlements, 0.0015 as Percentage),
        }),
        VARIABLE_0_15_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_0_15_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.0015 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_0_25_PERCENT_MIN_1KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_0_25_PERCENT_MIN_1KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.0025 as Percentage,
                priceFactory({ amountExcludingVat: 100 }),
            ),
        }),
        VARIABLE_1_PERCENT_MIN_1KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_1_PERCENT_MIN_1KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.01 as Percentage,
                priceFactory({ amountExcludingVat: 100 }),
            ),
        }),
        VARIABLE_1_5_PERCENT_MIN_1_5KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_1_5_PERCENT_MIN_1_5KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.015 as Percentage,
                priceFactory({ amountExcludingVat: 150 }),
            ),
        }),
        VARIABLE_2_PERCENT_MIN_2KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_2_PERCENT_MIN_2KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.02 as Percentage,
                priceFactory({ amountExcludingVat: 200 }),
            ),
        }),
        VARIABLE_2_PERCENT_MIN_2KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_2_PERCENT_MIN_2KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.02 as Percentage,
                priceFactory({ amountExcludingVat: 200 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_3_PERCENT_MIN_3KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_3_PERCENT_MIN_3KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.03 as Percentage,
                priceFactory({ amountExcludingVat: 300 }),
            ),
        }),
        VARIABLE_3_PERCENT_MIN_3KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_3_PERCENT_MIN_3KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.03 as Percentage,
                priceFactory({ amountExcludingVat: 300 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_4_PERCENT_MIN_4KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_4_PERCENT_MIN_4KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.04 as Percentage,
                priceFactory({ amountExcludingVat: 400 }),
            ),
        }),
        VARIABLE_4_PERCENT_MIN_4KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_4_PERCENT_MIN_4KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.04 as Percentage,
                priceFactory({ amountExcludingVat: 400 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_5_PERCENT_MIN_5KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_5_PERCENT_MIN_5KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.05 as Percentage,
                priceFactory({ amountExcludingVat: 500 }),
            ),
        }),
        VARIABLE_5_PERCENT_MIN_5KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_5_PERCENT_MIN_5KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.05 as Percentage,
                priceFactory({ amountExcludingVat: 500 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_6_PERCENT_MIN_6KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_6_PERCENT_MIN_6KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.06 as Percentage,
                priceFactory({ amountExcludingVat: 600 }),
            ),
        }),
        VARIABLE_6_PERCENT_MIN_6KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_6_PERCENT_MIN_6KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.06 as Percentage,
                priceFactory({ amountExcludingVat: 600 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_7_PERCENT_MIN_7KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_7_PERCENT_MIN_7KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.07 as Percentage,
                priceFactory({ amountExcludingVat: 700 }),
            ),
        }),
        VARIABLE_7_PERCENT_MIN_7KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_7_PERCENT_MIN_7KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.07 as Percentage,
                priceFactory({ amountExcludingVat: 700 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_10_PERCENT_MIN_3KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_MIN_3KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.1 as Percentage,
                priceFactory({ amountExcludingVat: 300 }),
            ),
        }),
        VARIABLE_10_PERCENT_MIN_3KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_MIN_3KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.1 as Percentage,
                priceFactory({ amountExcludingVat: 300 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_10_PERCENT_MIN_10KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_MIN_10KR_FEE,
            price: variableFeePerPayment(
                settlements,
                0.1 as Percentage,
                priceFactory({ amountExcludingVat: 1000 }),
            ),
        }),
        VARIABLE_10_PERCENT_MIN_10KR_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_MIN_10KR_FEE__EXCL_VAT,
            price: variableFeePerPayment(
                settlements,
                0.1 as Percentage,
                priceFactory({ amountExcludingVat: 1000 }),
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_1_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_1_PERCENT_FEE,
            price: percentageFee(settlements, 0.01 as Percentage),
        }),
        VARIABLE_1_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_1_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.01 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_2_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_2_PERCENT_FEE,
            price: percentageFee(settlements, 0.02 as Percentage),
        }),
        VARIABLE_2_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_2_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.02 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_3_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_3_PERCENT_FEE,
            price: percentageFee(settlements, 0.03 as Percentage),
        }),
        VARIABLE_3_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_3_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.03 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_4_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_4_PERCENT_FEE,
            price: percentageFee(settlements, 0.04 as Percentage),
        }),
        VARIABLE_4_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_4_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.04 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_5_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_5_PERCENT_FEE,
            price: percentageFee(settlements, 0.05 as Percentage),
        }),
        VARIABLE_5_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_5_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.05 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_6_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_6_PERCENT_FEE,
            price: percentageFee(settlements, 0.06 as Percentage),
        }),
        VARIABLE_6_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_6_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.06 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        VARIABLE_10_PERCENT_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_FEE,
            price: percentageFee(settlements, 0.1 as Percentage),
        }),
        VARIABLE_10_PERCENT_FEE__EXCL_VAT: hardCodedFeeFactory({
            ...hardCodedFeeMeta.VARIABLE_10_PERCENT_FEE__EXCL_VAT,
            price: percentageFee(
                settlements,
                0.1 as Percentage,
                "AMOUNT_EXCLUDING_VAT",
            ),
        }),
        FIXED_599_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_599_KR_FEE,
            price: priceFactory({ amountExcludingVat: 599_00 }),
        }),
        FIXED_895_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_895_KR_FEE,
            price: priceFactory({ amountExcludingVat: 895_00 }),
        }),
        FIXED_1_900_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_1_900_KR_FEE,
            price: priceFactory({ amountExcludingVat: 1900_00 }),
        }),
        FIXED_3_900_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_3_900_KR_FEE,
            price: priceFactory({ amountExcludingVat: 3900_00 }),
        }),
        FIXED_9_000_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_9_000_KR_FEE,
            price: priceFactory({ amountExcludingVat: 9000_00 }),
        }),
        FIXED_15_000_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_15_000_KR_FEE,
            price: priceFactory({ amountExcludingVat: 15000_00 }),
        }),
        FIXED_IPAD_RENTAL_150_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_IPAD_RENTAL_150_KR_FEE,
            price: priceFactory({ amountExcludingVat: 150_00 }),
        }),
        FIXED_PRINTER_RENTAL_150_KR_FEE: hardCodedFeeFactory({
            ...hardCodedFeeMeta.FIXED_PRINTER_RENTAL_150_KR_FEE,
            price: priceFactory({ amountExcludingVat: 150_00 }),
        }),
    };
};

const isHardCodedFee = (fee: unknown): fee is HardCodedFee => {
    return (fee as HardCodedFee)?._type === "HARD_CODED_FEE";
};

const isHardCodedFeeId = (feeId: unknown): feeId is HardCodedFeeId => {
    return (
        typeof feeId === "string" &&
        hardCodedFeeIds.includes(feeId as HardCodedFeeId)
    );
};

export {
    hardCodedFeeIds,
    hardCodedFeeIdZodEnum,
    hardCodedFeeMeta,
    hardCodedFeesForSettlements,
    hardCodedFeeZodSchema,
    isHardCodedFee,
    isHardCodedFeeId,
};
export type { HardCodedFee, HardCodedFeeId };
